"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";

interface FAQProps {
  isMobile: boolean;
}

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

export default function FAQ({ isMobile }: FAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqItems: FAQItem[] = [
    {
      question: "What do I get exactly?",
      answer: (
        <div className="space-y-2">
          <p>We focus on providing 4 key things:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li><span className="font-medium">Content courses</span> about AI coding & building LLM apps</li>
            <li><span className="font-medium">Premium tools</span> that help boost your building process</li>
            <li><span className="font-medium">Community</span> of top AI builders who can build together & help each other</li>
            <li><span className="font-medium">AI builder pack</span>: exclusive perks for essential tools to build AI products</li>
          </ol>
        </div>
      ),
    },
    {
      question: "Who is best suited for AI Builder Club?",
      answer: (
        <div className="space-y-2">
          <p>If you are:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li><span className="font-medium">Entrepreneurs & founders:</span> who wants to learn & launch AI products</li>
            <li><span className="font-medium">Engineers:</span> who wants to upskill into AI coding & AI engineer</li>
            <li><span className="font-medium">Product manager & designers:</span> who wants to learn how to build AI products</li>
            <li><span className="font-medium">Ops</span>: who wants to build internal tools using AI</li>
          </ol>
        </div>
      ),
    },
    {
      question: "I'm a beginner with no coding experience, is this still for me?",
      answer: (
        <div>
          <p>
            Yes,
          </p>
          <p>
          in fact, many of our members were complete beginners initially and successfully launched their first SaaS products. 
          </p>
          <br />
          <p>
            We have content for complete beginners to learn the fundamentals, and gradually becomes more advanced as you progress. We also have a community where help you build together with other members and get helps.
          </p>
        </div>
      ),
    },
    {
      question: "What if I get stuck during the course content?",
      answer: (
        <div>
          <p>
          We have a supportive community where you'll normally get answers from fellow members or instructors within 48 hours. 
        </p>
        <br />
        <p>
          For annual members, we even have a direct priority-support channel, where you can get answers more instantly. Our goal is to ensure no one gets left behind on their AI builder journey.
        </p>
        </div>
      ),
    },
    {
      question: "Will I learn how to use AI to build my app?",
      answer: (
        <p>
          Yes, you will learn all the fundamentals of building apps using AI, and not only that, we also have content from experts on Agents, LLM apps, and the latest AI technologies. Our courses cover everything from basic concepts to advanced implementation strategies, ensuring you can build sophisticated AI applications with confidence.
        </p>
      ),
    },
  ];

  return (
    <section className="py-12 md:py-20 section-container" id="faq">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="text-center mb-12">
          <h2 className={`${isMobile ? "text-4xl" : "text-5xl"} font-bold mb-6`}>
            Frequently Asked Questions
          </h2>
          <p className={`${isMobile ? "text-lg" : "text-xl"} text-white/70`}>
            Everything you need to know about AI Builder Club
          </p>
        </div>

        <div className="space-y-6">
          {faqItems.map((item, index) => (
            <div
              key={index}
              className="glass-card rounded-2xl overflow-hidden"
            >
              <button
                onClick={() => toggleQuestion(index)}
                className="w-full px-6 py-5 flex items-center justify-between bg-[#1A1A1A] hover:bg-[#252525] transition-colors text-left"
              >
                <h3 className={`${isMobile ? "text-lg" : "text-xl"} font-medium`}>
                  {item.question}
                </h3>
                <ChevronDown
                  className={`w-5 h-5 transition-transform duration-200 ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-6 py-5 bg-[#121212] text-white/80">
                  <div className={`${isMobile ? "text-sm" : "text-base"}`}>
                    {item.answer}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
} 